import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Ability } from '@casl/ability';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProjectsService } from '../../../../../core/projects.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components';
import { dailyDiarySubject } from '../daily-diary-ability.util';
import { DIARY_SECTION_PARAM } from '../daily-diary-mode.util';
import { DailyDiaryFormComponent, DailyDiarySection } from '../daily-diary-form/daily-diary-form.component';
import { RemarkSummaryCard, remarkSummaryCard } from '../daily-diary-remark.util';

/**
 * The diary equivalent of `app-daily-report-two`: renders one diary in the MAIN
 * BODY as collapsible component sections (reusing
 * `app-daily-report-expandable-block` and the report's summary-card patterns),
 * each of which opens a FOCUSED right panel for just that component.
 */
@Component({
  selector: 'app-daily-diary-detail',
  templateUrl: './daily-diary-detail.component.html',
  styleUrls: ['./daily-diary-detail.component.scss'],
  standalone: false,
})
export class DailyDiaryDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() diary: any;
  @Input() projectId!: string;
  @Input() siteId!: string;

  expandedSection = '';
  editable = false;

  /**
   * Shaped for `app-activity-summary` — the card the report's own Remarks
   * section uses: the body text as the title (clamped to two lines) with the
   * type beneath it and, when the remark carries one, a one-line location /
   * station summary (`locationSummary`, which only the diary sets), and its
   * design context read-only.
   */
  remarkCards: RemarkSummaryCard[] = [];

  /** The entry whose panel is open, highlighted like the report's cards. */
  selectedRemarkUuid: string | null = null;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private ability: Ability,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.expandedSection = qp[DIARY_SECTION_PARAM] || '';
        this.cdRef.detectChanges();
      });

    // The floating + in detail mode drops a component menu (Weather /
    // Remarks); picking one expands that section and opens its panel, exactly
    // as adding a component to a daily report does.
    this.projectsService.addDailyDiaryComponent
      .pipe(takeUntil(this.onDestroy))
      .subscribe((type: string) => {
        if (!this.editable) return;
        const section: DailyDiarySection = type === 'weather' ? 'weather' : 'remark';
        // navigate -> then -> setTimeout, the same shape as the report's
        // `addElementToDailyReport`. The extra macrotask matters: the panel
        // host only creates its ViewContainerRef once a change-detection pass
        // has rendered the open sidenav, and opening straight out of the
        // navigation promise gets there first and finds it undefined.
        this.router.navigate([], {
          // 'remark' has no section of its own — entries live under Remarks.
          queryParams: { [DIARY_SECTION_PARAM]: section === 'weather' ? 'weather' : 'remarks' },
          queryParamsHandling: 'merge',
        }).then(() => {
          setTimeout(() => {
            this.projectsService.isAnyFormDirty.next(false);
            // No uuid for a remark: the panel seeds itself a blank one.
            this.openPanel(section);
          });
        });
      });
  }

  ngOnChanges(): void {
    this.editable = this.diary?.status === 'draft'
      && this.ability.can('update', dailyDiarySubject(this.diary));
    this.remarkCards = (this.diary?.remarks || []).map(remarkSummaryCard);
  }

  get weather(): any {
    return this.diary?.weather || null;
  }

  get hasWeather(): boolean {
    const w = this.weather;
    if (!w) return false;
    return ['avgtemp', 'maxtemp', 'mintemp', 'wind', 'note'].some(k => w[k] !== null && w[k] !== undefined)
      || !!w?.condition?.text;
  }

  /** e.g. `47.3°F Rain`. */
  get weatherSummary(): string {
    if (!this.hasWeather) return 'Not added';
    const temp = this.weather?.avgtemp !== null && this.weather?.avgtemp !== undefined
      ? `${this.weather.avgtemp}°F`
      : '';
    return [temp, this.weather?.condition?.text].filter(Boolean).join(' ') || 'Added';
  }

  get remarksCount(): number {
    return this.diary?.remarks?.length || 0;
  }

  get workdayLabel(): string {
    return this.diary?.contractor_working ? 'WORK DAY' : 'NON-WORKDAY (CONTRACTOR NOT WORKING)';
  }

  /**
   * Same interaction as the report: the header only expands or collapses the
   * section and closes whatever panel was open. It does NOT open one — a
   * section can hold several entries, and which of them to edit is decided by
   * clicking a summary card.
   */
  handleExpansion(section: string): void {
    const collapsing = this.expandedSection === section;
    this.guardUnsavedChanges(() => {
      this.router.navigate([], {
        queryParams: { [DIARY_SECTION_PARAM]: collapsing ? null : section },
        queryParamsHandling: 'merge',
      }).then(() => {
        this.projectsService.isAnyFormDirty.next(false);
        this.selectedRemarkUuid = null;
        this.projectsService.closeRightPanel.next();
      });
    });
  }

  /** Clicking a summary card inside an expanded section, like the report does. */
  triggerOpenPanel(section: DailyDiarySection, remarkUuid?: string): void {
    this.guardUnsavedChanges(() => this.openPanel(section, remarkUuid));
  }

  private openPanel(section: DailyDiarySection, remarkUuid?: string): void {
    const diaryId = this.diary?.id || this.diary?.uuid;
    if (!diaryId) return;
    this.selectedRemarkUuid = section === 'remark' ? (remarkUuid ?? null) : null;
    this.projectsService.openRightPanel.next({
      component: DailyDiaryFormComponent,
      data: {
        projectId: this.projectId,
        siteId: this.siteId,
        diaryId,
        section,
        // Omitted when adding: the panel then seeds itself a blank remark.
        ...(remarkUuid ? { remarkUuid } : {}),
        mode: this.editable ? 'edit' : 'view',
      },
    });
  }

  private guardUnsavedChanges(run: () => void): void {
    if (!this.projectsService.isAnyFormDirty.getValue()) {
      run();
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Unsaved changes',
        `There are unsaved changes.<br/>Are you sure you want to discard?`,
      ),
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.projectsService.isAnyFormDirty.next(false);
      run();
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
